import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

const HeaderWithDropdown = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [closeTimeout, setCloseTimeout] = useState(null);
  const [aboutCloseTimeout, setAboutCloseTimeout] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const servicesMenu = [
    { name: 'AI Solutions', path: '/services/ai-solutions' },
    { name: 'Software Development', path: '/services/software-development' },
    { name: 'Data Science', path: '/services/data-science' },
    { name: 'Cloud Services', path: '/services/cloud-services' },
    { name: 'Cybersecurity', path: '/services/cybersecurity' },
    { name: 'Talent Solutions', path: '/services/talent-solutions' }
  ];

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  const scrollToContact = () => {
    if (location.pathname === '/') {
      const element = document.querySelector('#contact');
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    } else {
      navigate('/#contact');
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex flex-col">
            <img 
              src="/logo-oneit.png" 
              alt="OneIT Solutions, LLC," 
              className="logo-large"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-2">
            <Link
              to="/"
              className="relative text-gray-700 font-medium text-xl px-4 py-2 group transition-colors duration-300 hover:text-[#1A3E6E]"
            >
              Home
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#1E40AF] via-[#3B82F6] to-[#1E40AF] group-hover:w-full transition-all duration-500 ease-out"></span>
            </Link>

            {/* Services Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => {
                if (closeTimeout) clearTimeout(closeTimeout);
                setIsServicesOpen(true);
              }}
              onMouseLeave={() => {
                const timeout = setTimeout(() => setIsServicesOpen(false), 200);
                setCloseTimeout(timeout);
              }}
            >
              <button
                className="relative flex items-center text-gray-700 font-medium text-xl px-4 py-2 group transition-colors duration-300 hover:text-[#1A3E6E]"
              >
                Services
                <ChevronDown size={16} className="ml-1" />
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#1E40AF] via-[#3B82F6] to-[#1E40AF] group-hover:w-full transition-all duration-500 ease-out"></span>
              </button>
              {isServicesOpen && (
                <div 
                  className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-100 py-2 transition-all duration-200"
                  onMouseEnter={() => {
                    if (closeTimeout) clearTimeout(closeTimeout);
                  }}
                  onMouseLeave={() => {
                    const timeout = setTimeout(() => setIsServicesOpen(false), 200);
                    setCloseTimeout(timeout);
                  }}
                >
                  {servicesMenu.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => {
                        setIsServicesOpen(false);
                        handleNavigation(item.path);
                      }}
                      className="w-full text-left px-4 py-2 text-gray-700 hover:bg-[#1E40AF] hover:text-white transition-colors duration-200"
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/products"
              className="relative text-gray-700 font-medium text-lg px-4 py-2 group transition-colors duration-300 hover:text-[#1A3E6E]"
            >
              Products
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#1E40AF] via-[#3B82F6] to-[#1E40AF] group-hover:w-full transition-all duration-500 ease-out"></span>
            </Link>
            <Link
              to="/industries"
              className="relative text-gray-700 font-medium text-lg px-4 py-2 group transition-colors duration-300 hover:text-[#1A3E6E]"
            >
              Industries
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#1E40AF] via-[#3B82F6] to-[#1E40AF] group-hover:w-full transition-all duration-500 ease-out"></span>
            </Link>
            {/* About Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => {
                if (aboutCloseTimeout) clearTimeout(aboutCloseTimeout);
                setIsAboutOpen(true);
              }}
              onMouseLeave={() => {
                const timeout = setTimeout(() => setIsAboutOpen(false), 200);
                setAboutCloseTimeout(timeout);
              }}
            >
              <button
                className="relative flex items-center text-gray-700 font-medium text-lg px-4 py-2 group transition-colors duration-300 hover:text-[#1A3E6E]"
              >
                About
                <ChevronDown size={16} className="ml-1" />
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#1E40AF] via-[#3B82F6] to-[#1E40AF] group-hover:w-full transition-all duration-500 ease-out"></span>
              </button>
              {isAboutOpen && (
                <div 
                  className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 py-2 transition-all duration-200"
                  onMouseEnter={() => {
                    if (aboutCloseTimeout) clearTimeout(aboutCloseTimeout);
                  }}
                  onMouseLeave={() => {
                    const timeout = setTimeout(() => setIsAboutOpen(false), 200);
                    setAboutCloseTimeout(timeout);
                  }}
                >
                  <button
                    onClick={() => {
                      setIsAboutOpen(false);
                      navigate('/about');
                    }}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-[#1E40AF] hover:text-white transition-colors duration-200"
                  >
                    About Us
                  </button>
                  <button
                    onClick={() => {
                      setIsAboutOpen(false);
                      navigate('/careers');
                    }}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-[#1E40AF] hover:text-white transition-colors duration-200"
                  >
                    Careers
                  </button>
                  <button
                    onClick={() => {
                      setIsAboutOpen(false);
                      navigate('/contact');
                    }}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-[#1E40AF] hover:text-white transition-colors duration-200"
                  >
                    Contact
                  </button>
                </div>
              )}
            </div>
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button
              onClick={scrollToContact}
              className="bg-[#1A3E6E] hover:bg-[#1A3E6E] text-white px-6 py-2 rounded-md transition-colors duration-200"
            >
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-gray-700 hover:text-[#1A3E6E] transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col space-y-4">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-gray-700 hover:text-[#1A3E6E] font-medium transition-colors duration-200 px-4 py-2"
              >
                Home
              </Link>

              {/* Mobile Services */}
              <div>
                <button
                  onClick={() => setOpenDropdown(openDropdown === 'services' ? null : 'services')}
                  className="w-full flex items-center justify-between text-gray-700 hover:text-[#1A3E6E] font-medium transition-colors duration-200 px-4 py-2"
                >
                  Services
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${openDropdown === 'services' ? 'rotate-180' : ''}`}
                  />
                </button>
                {openDropdown === 'services' && (
                  <div className="pl-4 mt-2 space-y-2">
                    {servicesMenu.map((item) => (
                      <button
                        key={item.path}
                        onClick={() => handleNavigation(item.path)}
                        className="w-full text-left px-4 py-2 text-gray-600 hover:text-[#1A3E6E] transition-colors duration-200"
                      >
                        {item.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/products"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-gray-700 hover:text-[#1A3E6E] font-medium transition-colors duration-200 px-4 py-2"
              >
                Products
              </Link>

              <Link
                to="/industries"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-gray-700 hover:text-[#1A3E6E] font-medium transition-colors duration-200 px-4 py-2"
              >
                Industries
              </Link>

              {/* Mobile About */}
              <div>
                <button
                  onClick={() => setOpenDropdown(openDropdown === 'about' ? null : 'about')}
                  className="w-full flex items-center justify-between text-gray-700 hover:text-[#1A3E6E] font-medium transition-colors duration-200 px-4 py-2"
                >
                  About
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${openDropdown === 'about' ? 'rotate-180' : ''}`}
                  />
                </button>
                {openDropdown === 'about' && (
                  <div className="pl-4 mt-2 space-y-2">
                    <button
                      onClick={() => handleNavigation('/about')}
                      className="w-full text-left px-4 py-2 text-gray-600 hover:text-[#1A3E6E] transition-colors duration-200"
                    >
                      About Us
                    </button>
                    <button
                      onClick={() => handleNavigation('/careers')}
                      className="w-full text-left px-4 py-2 text-gray-600 hover:text-[#1A3E6E] transition-colors duration-200"
                    >
                      Careers
                    </button>
                    <button
                      onClick={() => handleNavigation('/contact')}
                      className="w-full text-left px-4 py-2 text-gray-600 hover:text-[#1A3E6E] transition-colors duration-200"
                    >
                      Contact
                    </button>
                  </div>
                )}
              </div>
              <div className="px-4 pt-2">
                <Button
                  onClick={scrollToContact}
                  className="w-full bg-[#1A3E6E] hover:bg-[#1A3E6E] text-white px-6 py-2 rounded-md transition-colors duration-200"
                >
                  Get Started
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default HeaderWithDropdown;
